import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { animate, useMotionValue, useReducedMotion } from "framer-motion";
import { BOARD, MAP_FOCUS } from "../../data/board";
import { MAP, clamp, clampPan, fitScale, homeScale, viewForPoint, zoomToward } from "../../utils/mapCamera";
import { POINTER, POINTER_EVENT } from "../../utils/pointer";

const CameraContext = createContext(null);

export const CameraProvider = ({ children }) => {
  const reduceMotion = useReducedMotion();
  const startVw = typeof window !== "undefined" ? window.innerWidth : 1440;
  const startVh = typeof window !== "undefined" ? window.innerHeight : 900;
  const startScale = homeScale(startVw);
  const start = viewForPoint(MAP_FOCUS.x, MAP_FOCUS.y, startScale, startVw, startVh);
  const x = useMotionValue(start.x);
  const y = useMotionValue(start.y);
  const scale = useMotionValue(startScale);
  const vw = useMotionValue(startVw);
  const vh = useMotionValue(startVh);
  const controlsRef = useRef([]);
  const spaceHeldRef = useRef(false);
  const pointerModeRef = useRef(POINTER.GRAB);
  const [cssZoom] = useState(() =>
    typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("zoom", "1")
  );

  const stopMotion = useCallback(() => {
    controlsRef.current.forEach((control) => control.stop());
    controlsRef.current = [];
  }, []);

  const setPointerMode = useCallback((mode) => {
    if (pointerModeRef.current === mode) return;
    pointerModeRef.current = mode;
    document.documentElement.dataset.boardPointer = mode;
    window.dispatchEvent(new CustomEvent(POINTER_EVENT, { detail: mode }));
  }, []);

  const setView = useCallback(
    (nextX, nextY, nextScale) => {
      stopMotion();
      const s = clamp(nextScale, MAP.minScale, MAP.maxScale);
      const pan = clampPan(nextX, nextY, s, vw.get(), vh.get());
      x.set(pan.x);
      y.set(pan.y);
      scale.set(s);
    },
    [scale, stopMotion, vh, vw, x, y]
  );

  const applyView = useCallback(
    (nextX, nextY, nextScale, withMotion = false) => {
      const s = clamp(nextScale, MAP.minScale, MAP.maxScale);
      const clamped = clampPan(nextX, nextY, s, vw.get(), vh.get());
      stopMotion();
      if (!withMotion || reduceMotion) {
        x.set(clamped.x);
        y.set(clamped.y);
        scale.set(s);
        return;
      }
      const panX = animate(x, clamped.x, { type: "spring", stiffness: 220, damping: 28, mass: 0.7 });
      const panY = animate(y, clamped.y, { type: "spring", stiffness: 220, damping: 28, mass: 0.7 });
      const zoom = animate(scale, s, { type: "spring", stiffness: 240, damping: 30, mass: 0.65 });
      controlsRef.current = [panX, panY, zoom];
    },
    [reduceMotion, scale, stopMotion, vh, vw, x, y]
  );

  const resetView = useCallback(
    (withMotion = true) => {
      const home = homeScale(vw.get());
      const next = viewForPoint(MAP_FOCUS.x, MAP_FOCUS.y, home, vw.get(), vh.get());
      applyView(next.x, next.y, home, withMotion);
    },
    [applyView, vh, vw]
  );

  const showWholeBoard = useCallback(
    (withMotion = true) => {
      const fit = fitScale(vw.get(), vh.get());
      const next = viewForPoint(BOARD.width / 2, BOARD.height / 2, fit, vw.get(), vh.get());
      applyView(next.x, next.y, fit, withMotion);
    },
    [applyView, vh, vw]
  );

  const zoomAt = useCallback(
    (px, py, nextScale) => {
      const current = scale.get() || 1;
      const zoomed = zoomToward({
        x: x.get(),
        y: y.get(),
        scale: current,
        nextScale,
        px,
        py,
        vw: vw.get(),
        vh: vh.get(),
      });
      setView(zoomed.x, zoomed.y, clamp(nextScale, MAP.minScale, MAP.maxScale));
    },
    [scale, setView, vh, vw, x, y]
  );

  const zoomBy = useCallback(
    (factor, originX, originY) => {
      const px = originX ?? vw.get() / 2;
      const py = originY ?? vh.get() / 2;
      zoomAt(px, py, scale.get() * factor);
    },
    [scale, vh, vw, zoomAt]
  );

  const panBy = useCallback(
    (dx, dy) => {
      stopMotion();
      const next = clampPan(x.get() + dx, y.get() + dy, scale.get(), vw.get(), vh.get());
      x.set(next.x);
      y.set(next.y);
    },
    [scale, stopMotion, vh, vw, x, y]
  );

  const clampCurrent = useCallback(() => {
    const next = clampPan(x.get(), y.get(), scale.get(), vw.get(), vh.get());
    x.set(next.x);
    y.set(next.y);
  }, [scale, vh, vw, x, y]);

  const flyToBoard = useCallback(
    (boardX, boardY, nextScale, withMotion = true) => {
      const s = clamp(nextScale ?? scale.get(), MAP.minScale, MAP.maxScale);
      const view = viewForPoint(boardX, boardY, s, vw.get(), vh.get());
      applyView(view.x, view.y, s, withMotion);
    },
    [applyView, scale, vh, vw]
  );

  useEffect(() => {
    document.documentElement.dataset.boardPointer = POINTER.GRAB;
    return () => {
      stopMotion();
      delete document.documentElement.dataset.boardPointer;
    };
  }, [stopMotion]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const target = event.target;
      if (isFormField(target)) return;
      // An open evidence sheet owns the keyboard; the board behind it stays put.
      if (document.body.classList.contains("modal-open")) return;

      if (event.code === "Space") {
        if (!event.repeat) {
          spaceHeldRef.current = true;
          if (pointerModeRef.current !== POINTER.GRABBING) setPointerMode(POINTER.GRAB);
        }
        event.preventDefault();
        return;
      }

      if (event.altKey) return;

      if (event.key === "=" || event.key === "+") {
        event.preventDefault();
        zoomBy(MAP.zoomStep);
        setPointerMode(POINTER.ZOOM_IN);
        return;
      }
      if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        zoomBy(1 / MAP.zoomStep);
        setPointerMode(POINTER.ZOOM_OUT);
        return;
      }
      if (event.key === "0" || event.key === "Escape") {
        event.preventDefault();
        resetView(true);
        return;
      }

      if (event.metaKey || event.ctrlKey) return;

      const step = 72;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        panBy(step, 0);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        panBy(-step, 0);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        panBy(0, step);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        panBy(0, -step);
      }
    };

    const clearSpace = () => {
      spaceHeldRef.current = false;
      if (pointerModeRef.current !== POINTER.GRABBING) setPointerMode(POINTER.GRAB);
    };

    const onKeyUp = (event) => {
      if (event.code !== "Space") return;
      clearSpace();
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", clearSpace);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", clearSpace);
    };
  }, [panBy, resetView, setPointerMode, zoomBy]);

  const value = useMemo(
    () => ({
      x,
      y,
      scale,
      vw,
      vh,
      cssZoom,
      spaceHeldRef,
      pointerModeRef,
      setPointerMode,
      setView,
      panBy,
      resetView,
      showWholeBoard,
      zoomAt,
      zoomBy,
      clampCurrent,
      flyToBoard,
      reduceMotion: Boolean(reduceMotion),
    }),
    [clampCurrent, cssZoom, flyToBoard, panBy, reduceMotion, resetView, scale, setPointerMode, setView, showWholeBoard, vh, vw, x, y, zoomAt, zoomBy]
  );

  return <CameraContext.Provider value={value}>{children}</CameraContext.Provider>;
};

const isFormField = (target) => {
  if (!target || !target.tagName) return false;
  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
};

export const SkipToCase = () => {
  const { resetView } = useCamera();
  return (
    <a
      className="skip-link"
      href="#overview"
      onClick={(e) => {
        e.preventDefault();
        resetView(true);
      }}
    >
      Skip to case file
    </a>
  );
};

export const useCamera = () => {
  const ctx = useContext(CameraContext);
  if (!ctx) {
    throw new Error("useCamera must be used inside CameraProvider");
  }
  return ctx;
};
